import { CurrencyPipe, DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  output,
  signal,
  viewChild,
} from '@angular/core';
import {
  ConfirmModalComponent,
  InfoBannerComponent,
  ModalComponent,
  SegmentedControlComponent,
  SegmentedControlItem,
  SpinnerComponent,
  TagVariant,
} from '@catch/general-ui';
import { ToastsService } from '@catch/services';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { Store } from '@ngrx/store';
import { collapse } from '../../../assets/animations/animations';
import { StripeAdapter } from '../../adapters/stripe.adapter';
import {
  FirstBookingPaymentMethod,
  StripeBillingInterval,
  StripeProductType,
} from '../../enums/stripe.enum';
import { InvoiceBookingConfirmData } from '../../interfaces/invoice-booking-confirm';
import { StripeProduct } from '../../interfaces/stripe';
import { BookingConfirmationService } from '../../services/booking-confirmation.service';
import { selectSubscription } from '../../store/subscription/subscription.reducer';
import { isImmediatePlanChange } from '../../utils/is-immediate-plan-change.util';
import { translateBillingPeriod } from '../../utils/billing-period-display.util';
import {
  AgentsPlanSelectorComponent,
  CUSTOM_AGENTS_OPTION,
} from '../booking-selectors/agents-plan-selector/agents-plan-selector.component';
import { AtsAddonSelectorComponent } from '../booking-selectors/ats-addon-selector/ats-addon-selector.component';
import { BasePlanSelectorComponent } from '../booking-selectors/base-plan-selector/base-plan-selector.component';
import { ButtonComponent } from '../button/button.component';
import { InvoiceBookingConfirmModalComponent } from '../invoice-booking-confirm-modal/invoice-booking-confirm-modal.component';
import { PaymentCheckoutComponent } from '../payment-checkout/payment-checkout.component';
import { PaymentMethodChoiceComponent } from '../payment-method-choice/payment-method-choice.component';

interface CombinedBookingItem {
  product: StripeProduct;
  quantity: number;
}

const COMBINED_FREE_PRICE_ID = 'free';

const COMBINED_FREE_BASE_PRODUCT: StripeProduct = {
  id: COMBINED_FREE_PRICE_ID,
  product: {
    id: 'prod_free',
    name: 'Free',
    description: '',
  },
  unit_amount: 0,
  currency: 'eur',
  metadata: {
    sub_level: '0',
    max_jobs: '1',
  },
};

@Component({
  selector: 'app-combined-subscription-booking-modal',
  imports: [
    ButtonComponent,
    CurrencyPipe,
    DatePipe,
    AgentsPlanSelectorComponent,
    AtsAddonSelectorComponent,
    BasePlanSelectorComponent,
    ConfirmModalComponent,
    InfoBannerComponent,
    InvoiceBookingConfirmModalComponent,
    ModalComponent,
    PaymentCheckoutComponent,
    PaymentMethodChoiceComponent,
    SegmentedControlComponent,
    SpinnerComponent,
    TranslocoPipe,
  ],
  templateUrl: './combined-subscription-booking-modal.component.html',
  styleUrl: './combined-subscription-booking-modal.component.scss',
  animations: [collapse],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class CombinedSubscriptionBookingModalComponent {
  private readonly modal = viewChild.required<ModalComponent>('modal');
  private readonly invoiceConfirmModal =
    viewChild.required<InvoiceBookingConfirmModalComponent>('invoiceConfirm');
  private readonly paymentMethodChoice =
    viewChild<PaymentMethodChoiceComponent>('paymentMethodChoice');
  private readonly downgradeConfirmModal = viewChild<ConfirmModalComponent>(
    'downgradeConfirmModal',
  );
  readonly paymentCheckout = viewChild(PaymentCheckoutComponent);

  private readonly stripeAdapter = inject(StripeAdapter);
  private readonly toastsService = inject(ToastsService);
  private readonly transloco = inject(TranslocoService);
  private readonly bookingConfirmation = inject(BookingConfirmationService);
  private readonly store = inject(Store);

  readonly booked = output<void>();
  readonly closed = output<void>();
  private readonly subscription = this.store.selectSignal(selectSubscription);
  private readonly allowUnchangedSelection = signal(false);

  readonly CUSTOM_AGENTS_OPTION = CUSTOM_AGENTS_OPTION;
  readonly billingIntervalOptions: ReadonlyArray<
    SegmentedControlItem<StripeBillingInterval>
  > = [
    {
      label:
        'company-tool.snippets.combined-subscription-booking-modal.interval-month',
      value: StripeBillingInterval.MONTH,
    },
    {
      label:
        'company-tool.snippets.combined-subscription-booking-modal.interval-year',
      value: StripeBillingInterval.YEAR,
      tag: {
        label:
          'company-tool.snippets.combined-subscription-booking-modal.interval-year-save',
        type: TagVariant.GREEN,
      },
    },
  ];

  readonly billingInterval = signal(StripeBillingInterval.YEAR);
  readonly baseProducts = signal<StripeProduct[]>([]);
  readonly agentsProducts = signal<StripeProduct[]>([]);
  readonly atsProducts = signal<StripeProduct[]>([]);
  readonly catalogsLoading = signal(false);
  readonly selectedBasePriceId = signal<string | null>(null);
  readonly selectedAgentsOption = signal<string | null>(null);
  readonly selectedAtsPriceId = signal<string | null>(null);
  readonly includeAgents = signal(false);
  readonly includeAts = signal(false);
  readonly customAgentCount = signal(1);
  readonly isPaymentStep = signal(false);
  readonly isPaymentMethodStep = signal(false);
  readonly bookingInProgress = signal(false);
  readonly selectionLocked = signal(false);

  private pendingCheckoutSessionId: string | null = null;
  private pendingCheckoutPriceId: string | null = null;
  private pendingCheckoutQuantity = 1;
  private pendingPackageItems: Array<{ priceId: string; quantity: number }> =
    [];
  private catalogRequestId = 0;
  private readonly catalogCache = new Map<
    StripeBillingInterval,
    {
      base: StripeProduct[];
      agents: StripeProduct[];
      ats: StripeProduct[];
    }
  >();

  readonly singleAgentProduct = computed(
    () =>
      this.agentsProducts().find(
        (product) => this.maxAgentsOf(product) === 1,
      ) ?? null,
  );

  readonly agentsIsCustomCurrent = computed(() => {
    const agents =
      this.subscription()?.subscriptionSummary?.agentsSubscription;
    const priceId = agents?.priceId ?? null;
    return (
      !!priceId &&
      this.singleAgentProduct()?.id === priceId &&
      this.customAgentCount() === Math.max(1, agents?.quantity ?? 1)
    );
  });

  readonly currentAgentsPackagePriceId = computed(() => {
    const priceId =
      this.subscription()?.subscriptionSummary?.agentsSubscription?.priceId ??
      null;
    if (!priceId || this.singleAgentProduct()?.id === priceId) {
      return null;
    }
    return this.agentsProducts().some((product) => product.id === priceId)
      ? priceId
      : null;
  });

  readonly selectedBaseProduct = computed(() =>
    this.findProduct(this.baseProducts(), this.selectedBasePriceId()),
  );

  readonly selectedAgentsProduct = computed(() => {
    const option = this.selectedAgentsOption();
    if (option === this.CUSTOM_AGENTS_OPTION) {
      return this.singleAgentProduct();
    }
    return this.findProduct(this.agentsProducts(), option);
  });

  readonly selectedAtsProduct = computed(() =>
    this.findProduct(this.atsProducts(), this.selectedAtsPriceId()),
  );

  readonly canBook = computed(
    () =>
      !this.catalogsLoading() &&
      !this.bookingInProgress() &&
      !!this.selectedBaseProduct() &&
      (!this.includeAgents() || !!this.selectedAgentsProduct()) &&
      (!this.includeAts() || !!this.selectedAtsProduct()) &&
      (this.allowUnchangedSelection() || this.hasSelectionChanges()),
  );

  readonly hasPaidPackage = computed(() => {
    const summary = this.subscription()?.subscriptionSummary;
    if (!summary) {
      return false;
    }
    if (summary.agentsSubscription?.name) {
      return true;
    }
    if (summary.atsSubscription?.name) {
      return true;
    }
    if (summary.isFree) {
      return false;
    }
    return (summary.baseSubscription?.cost ?? 0) > 0;
  });

  readonly modalTitleKey = computed(() => {
    if (this.isPaymentMethodStep()) {
      return 'company-tool.snippets.payment-method-choice.title';
    }
    return this.hasPaidPackage()
      ? 'company-tool.snippets.combined-subscription-booking-modal.title-change'
      : 'company-tool.snippets.combined-subscription-booking-modal.title';
  });

  readonly introKey = computed(() =>
    this.hasPaidPackage()
      ? 'company-tool.snippets.combined-subscription-booking-modal.intro-change'
      : 'company-tool.snippets.combined-subscription-booking-modal.intro',
  );

  readonly confirmLabelKey = computed(() => {
    if (this.isPaymentMethodStep()) {
      return 'company-tool.snippets.combined-subscription-booking-modal.continue';
    }
    if (this.allowUnchangedSelection() && !this.hasSelectionChanges()) {
      return 'company-tool.snippets.combined-subscription-booking-modal.continue';
    }
    return this.hasPaidPackage()
      ? 'company-tool.snippets.combined-subscription-booking-modal.save'
      : 'company-tool.snippets.combined-subscription-booking-modal.book';
  });

  readonly needsPaymentMethodChoice = computed(() =>
    this.bookingConfirmation.needsPaymentMethodChoice(),
  );

  readonly laterChangeDate = computed(
    () =>
      this.subscription()?.subscriptionSummary?.baseSubscription
        ?.cancelAtIfNow ??
      this.subscription()?.subscriptionSummary?.agentsSubscription
        ?.cancelAtIfNow ??
      null,
  );

  readonly currentBaseCatalogPriceId = computed(() => {
    if (this.currentlyHasPaidBase()) {
      return this.currentBasePriceId();
    }
    if (this.allowUnchangedSelection()) {
      return null;
    }
    return COMBINED_FREE_PRICE_ID;
  });

  readonly monthlyTotal = computed(() => {
    let total = this.monthlyAmount(this.selectedBaseProduct());
    if (this.includeAgents()) {
      const quantity =
        this.selectedAgentsOption() === this.CUSTOM_AGENTS_OPTION
          ? this.customAgentCount()
          : 1;
      total += this.monthlyAmount(this.selectedAgentsProduct()) * quantity;
    }
    if (this.includeAts()) {
      total += this.monthlyAmount(this.selectedAtsProduct());
    }
    return total;
  });

  /**
   * Translated period label for the combined total.
   * @returns {string}
   */
  readonly totalPeriodHint = computed(() =>
    translateBillingPeriod(
      this.transloco,
      this.selectedBaseProduct(),
      this.billingInterval(),
    ),
  );

  /**
   * Opens the combined booking modal and loads all catalogs.
   * @param {{ allowUnchangedSelection?: boolean }} [options] Open options
   * @param {boolean} [options.allowUnchangedSelection] Whether the current package (including Free) can be confirmed
   * @returns {void}
   */
  open(options?: { allowUnchangedSelection?: boolean }): void {
    this.reset();
    this.allowUnchangedSelection.set(!!options?.allowUnchangedSelection);
    const currentInterval = this.currentPackageInterval();
    if (currentInterval) {
      this.billingInterval.set(currentInterval);
    }
    this.loadCatalogs();
    this.modal().open();
  }

  /**
   * Closes the combined booking modal.
   * @returns {void}
   */
  close(): void {
    this.modal().close();
  }

  /**
   * Resets checkout state and notifies listeners that the modal was closed.
   * @returns {void}
   */
  onModalClosed(): void {
    this.backToSelection();
    this.closed.emit();
  }

  /**
   * Changes the shared billing interval from cache when available.
   * @param {StripeBillingInterval} interval Selected billing interval
   * @returns {void}
   */
  setBillingInterval(interval: StripeBillingInterval): void {
    if (
      this.bookingInProgress() ||
      this.selectionLocked() ||
      this.catalogsLoading() ||
      interval === this.billingInterval()
    ) {
      return;
    }
    const previousBase = this.selectedBaseProduct();
    const previousAgents = this.selectedAgentsProduct();
    const previousAts = this.selectedAtsProduct();
    const previousAgentsOption = this.selectedAgentsOption();
    const previousCount = this.customAgentCount();
    const previousIncludeAgents = this.includeAgents();
    const previousIncludeAts = this.includeAts();

    this.billingInterval.set(interval);
    const cached = this.catalogCache.get(interval);
    if (cached) {
      this.applyCatalogSlice(cached);
      this.restoreSelectionAfterIntervalSwitch({
        previousBase,
        previousAgents,
        previousAts,
        previousAgentsOption,
        previousCount,
        previousIncludeAgents,
        previousIncludeAts,
      });
      return;
    }
    this.loadCatalogs();
  }

  /**
   * Updates whether Agents should be included.
   * @param {Event} event Checkbox change event
   * @returns {void}
   */
  setIncludeAgents(event: Event): void {
    this.includeAgents.set((event.target as HTMLInputElement).checked);
  }

  /**
   * Updates whether the ATS Add-On should be included.
   * @param {Event} event Checkbox change event
   * @returns {void}
   */
  setIncludeAts(event: Event): void {
    this.includeAts.set((event.target as HTMLInputElement).checked);
  }

  /**
   * Returns a product display name.
   * @param {StripeProduct} product Stripe catalog product
   * @returns {string}
   */
  productTitle(product: StripeProduct): string {
    return product.product?.name ?? product.id;
  }

  /**
   * Returns the billing-cycle display amount in major currency units.
   * @param {StripeProduct | null} product Stripe catalog product
   * @returns {number}
   */
  monthlyAmount(product: StripeProduct | null): number {
    return product ? product.unit_amount / 100 : 0;
  }

  /**
   * Reads the number of Agents represented by a product.
   * @param {StripeProduct} product Stripe catalog product
   * @returns {number}
   */
  maxAgentsOf(product: StripeProduct): number {
    const directValue = +(product.metadata?.max_agents ?? 0);
    if (directValue > 0) {
      return directValue;
    }
    return +(product.product?.metadata?.max_agents ?? 0);
  }

  /**
   * Starts the combined booking after optional invoice confirmation.
   * @returns {void}
   */
  confirmBooking(): void {
    if (!this.canBook()) {
      return;
    }
    if (this.hasPaidPackage() && this.selectionHasLaterChange()) {
      this.selectionLocked.set(true);
      this.downgradeConfirmModal()?.modal.open();
      return;
    }
    this.continueConfirmedBooking();
  }

  /**
   * Continues after the period-end confirm.
   * @returns {void}
   */
  confirmLaterChanges(): void {
    this.selectionLocked.set(false);
    this.continueConfirmedBooking();
  }

  /**
   * Continues booking after invoice confirmation.
   * @returns {void}
   */
  onInvoiceBookingConfirmed(): void {
    this.selectionLocked.set(false);
    if (this.bookingConfirmation.invoicePayment()) {
      this.submitPackage();
      return;
    }
    this.enableInvoiceThenSubmit();
  }

  /**
   * Continues after the payment-method step: Checkout or invoice activation.
   * @returns {void}
   */
  confirmPaymentMethod(): void {
    const choice = this.paymentMethodChoice();
    if (this.bookingInProgress() || !this.canBook() || !choice) {
      return;
    }
    this.bookingInProgress.set(true);
    choice.prepareSelection().subscribe({
      next: (method) => {
        this.bookingInProgress.set(false);
        if (method === FirstBookingPaymentMethod.INVOICE) {
          this.openInvoiceConfirm();
          return;
        }
        this.submitPackage();
      },
      error: () => this.bookingInProgress.set(false),
    });
  }

  /**
   * Shows invoice confirm when needed, otherwise submits the package.
   * @returns {void}
   */
  private continueConfirmedBooking(): void {
    if (this.allowUnchangedSelection() && !this.hasSelectionChanges()) {
      this.close();
      return;
    }
    const items = this.selectedPaidItems();
    if (items.length === 0) {
      this.submitPackage();
      return;
    }
    this.bookingInProgress.set(true);
    this.bookingConfirmation.resolveNeedsPaymentMethodChoice().subscribe({
      next: (needsChoice) => {
        this.bookingInProgress.set(false);
        if (needsChoice) {
          this.isPaymentMethodStep.set(true);
          return;
        }
        if (this.bookingConfirmation.invoicePayment()) {
          this.openInvoiceConfirm();
          return;
        }
        this.submitPackage();
      },
      error: () => {
        this.bookingInProgress.set(false);
        this.submitPackage();
      },
    });
  }

  /**
   * Enables invoice payment, then books the selected package.
   * @returns {void}
   */
  private enableInvoiceThenSubmit(): void {
    this.bookingInProgress.set(true);
    this.bookingConfirmation.enableInvoicePayment().subscribe({
      next: () => this.submitPackage(),
      error: () => this.bookingInProgress.set(false),
    });
  }

  /**
   * Opens the invoice booking confirmation for the current selection.
   * @returns {void}
   */
  private openInvoiceConfirm(): void {
    const items = this.selectedPaidItems();
    if (items.length === 0) {
      this.submitPackage();
      return;
    }
    this.selectionLocked.set(true);
    this.invoiceConfirmModal().open(this.createInvoiceConfirmData(items));
  }

  /**
   * Applies the selected package. Checkout includes all paid products on one invoice.
   * @returns {void}
   */
  private submitPackage(): void {
    const items = this.selectedPaidItems().map((item) => ({
      priceId: item.product.id,
      quantity: item.quantity,
    }));
    this.bookingInProgress.set(true);
    this.stripeAdapter.applyPackage(items, window.location.href).subscribe({
      next: (result) => {
        if (result.needsCheckout && result.clientSecret) {
          this.pendingPackageItems = items;
          this.pendingCheckoutPriceId =
            result.priceId ?? items[0]?.priceId ?? null;
          this.pendingCheckoutQuantity =
            items.find((item) => item.priceId === this.pendingCheckoutPriceId)
              ?.quantity ?? 1;
          this.pendingCheckoutSessionId = result.checkoutSessionId ?? null;
          this.isPaymentStep.set(true);
          this.mountCheckout(result.clientSecret, result.checkoutSessionId);
          return;
        }
        this.finishPackage(!!result.upgraded || !!result.invoiceBooked);
      },
      error: () => {
        this.toastsService.alert(
          'company-tool.views.settings.company.payment.sections.subscriptions.action-error',
        );
        this.bookingInProgress.set(false);
      },
    });
  }

  /**
   * Closes after a package sync.
   * @param {boolean} billedNow Whether anything billed immediately
   * @returns {void}
   */
  private finishPackage(billedNow: boolean): void {
    if (billedNow) {
      this.bookingConfirmation.showSuccessToast();
    } else {
      this.toastsService.info(
        'company-tool.snippets.combined-subscription-booking-modal.success-schedule',
      );
    }
    this.booked.emit();
    this.bookingInProgress.set(false);
    this.close();
    this.reset();
  }

  /**
   * Applies the completed Checkout (all line items), then syncs the package.
   * @returns {void}
   */
  onPaymentCompleted(): void {
    this.bookingConfirmation.rememberPaymentMethodOnFile();
    const priceId = this.pendingCheckoutPriceId;
    const items = this.pendingPackageItems;
    if (!priceId) {
      this.finishPackage(true);
      return;
    }
    this.stripeAdapter
      .applyBookedPrice(
        priceId,
        this.pendingCheckoutQuantity,
        this.pendingCheckoutSessionId ?? undefined,
      )
      .subscribe({
        next: () => this.syncRemainingPackage(items),
        error: () => this.syncRemainingPackage(items),
      });
  }

  /**
   * Syncs the selected package after Checkout. Combined first bookings already
   * contain every item, so this is a no-op unless something still needs to attach.
   * @param {Array<{ priceId: string; quantity: number }>} items Desired package
   * @returns {void}
   */
  private syncRemainingPackage(
    items: Array<{ priceId: string; quantity: number }>,
  ): void {
    this.stripeAdapter.applyPackage(items, window.location.href).subscribe({
      next: () => this.finishPackage(true),
      error: () => this.finishPackage(true),
    });
  }

  /**
   * Returns from payment or payment-method step to the previous step.
   * @returns {void}
   */
  backToSelection(): void {
    if (this.isPaymentStep() && this.needsPaymentMethodChoice()) {
      this.paymentCheckout()?.destroy();
      this.isPaymentStep.set(false);
      this.isPaymentMethodStep.set(true);
      this.bookingInProgress.set(false);
      this.pendingCheckoutSessionId = null;
      this.pendingCheckoutPriceId = null;
      this.pendingPackageItems = [];
      return;
    }
    this.paymentCheckout()?.destroy();
    this.isPaymentStep.set(false);
    this.isPaymentMethodStep.set(false);
    this.bookingInProgress.set(false);
    this.pendingCheckoutSessionId = null;
    this.pendingCheckoutPriceId = null;
    this.pendingPackageItems = [];
  }

  /**
   * Builds selected paid package items in booking order.
   * @returns {CombinedBookingItem[]}
   */
  private selectedPaidItems(): CombinedBookingItem[] {
    const items: CombinedBookingItem[] = [];
    const baseProduct = this.selectedBaseProduct();
    if (baseProduct && !this.isFreeBase(baseProduct)) {
      items.push({
        product: baseProduct,
        quantity: 1,
      });
    }
    const agentsProduct = this.selectedAgentsProduct();
    if (this.includeAgents() && agentsProduct) {
      items.push({
        product: agentsProduct,
        quantity:
          this.selectedAgentsOption() === this.CUSTOM_AGENTS_OPTION
            ? this.customAgentCount()
            : 1,
      });
    }
    const atsProduct = this.selectedAtsProduct();
    if (this.includeAts() && atsProduct) {
      items.push({
        product: atsProduct,
        quantity: 1,
      });
    }
    return items;
  }

  /**
   * Creates a combined invoice confirmation summary.
   * @param {CombinedBookingItem[]} items Selected paid items
   * @returns {InvoiceBookingConfirmData}
   */
  private createInvoiceConfirmData(
    items: CombinedBookingItem[],
  ): InvoiceBookingConfirmData {
    const lines = items.map((item) => ({
      name: this.productTitle(item.product),
      priceLabel: this.bookingConfirmation.formatCents(
        item.product.unit_amount * item.quantity,
        item.product.currency,
      ),
    }));
    const netAmountInCents = items.reduce(
      (total, item) => total + item.product.unit_amount * item.quantity,
      0,
    );
    return {
      ...this.bookingConfirmation.toConfirmData(
        lines,
        netAmountInCents,
        items[0]?.product.currency,
        translateBillingPeriod(
          this.transloco,
          items[0]?.product,
          this.billingInterval(),
        ),
      ),
      showProrationHint: this.hasPaidPackage(),
    };
  }

  /**
   * Mounts embedded checkout after apply-package prepared a session.
   * @param {string} clientSecret Stripe checkout client secret
   * @param {string | null | undefined} checkoutSessionId Checkout session id
   * @returns {void}
   */
  private mountCheckout(
    clientSecret: string,
    checkoutSessionId?: string | null,
  ): void {
    const checkout = this.paymentCheckout();
    if (!checkout) {
      setTimeout(() => this.mountCheckout(clientSecret, checkoutSessionId), 10);
      return;
    }
    void checkout.mountFromClientSecret(clientSecret, checkoutSessionId);
  }

  /**
   * Loads Base, Agents and ATS catalogs for both intervals.
   * @returns {void}
   */
  private loadCatalogs(): void {
    this.catalogsLoading.set(true);
    this.baseProducts.set([]);
    this.agentsProducts.set([]);
    this.atsProducts.set([]);
    this.selectedBasePriceId.set(null);
    this.selectedAgentsOption.set(null);
    this.selectedAtsPriceId.set(null);
    this.customAgentCount.set(1);

    const requestId = ++this.catalogRequestId;
    let remainingCatalogs = 3;
    let baseAll: StripeProduct[] = [];
    let agentsAll: StripeProduct[] = [];
    let atsAll: StripeProduct[] = [];

    const catalogLoaded = (): void => {
      remainingCatalogs -= 1;
      if (remainingCatalogs > 0) {
        return;
      }
      if (requestId !== this.catalogRequestId) {
        return;
      }
      this.fillCatalogCache(baseAll, agentsAll, atsAll);
      this.applyCatalogSlice(
        this.catalogCache.get(this.billingInterval()) ?? {
          base: [COMBINED_FREE_BASE_PRODUCT],
          agents: [],
          ats: [],
        },
      );
      this.prefillFromSummary();
      this.catalogsLoading.set(false);
    };

    this.stripeAdapter.getSelfBookingProducts(StripeProductType.BASIC).subscribe({
      next: (products) => {
        baseAll = products;
        catalogLoaded();
      },
      error: () => catalogLoaded(),
    });

    this.stripeAdapter
      .getSelfBookingProducts(StripeProductType.AGENTS)
      .subscribe({
        next: (products) => {
          agentsAll = products.filter((product) => product.unit_amount > 0);
          catalogLoaded();
        },
        error: () => catalogLoaded(),
      });

    this.stripeAdapter
      .getSelfBookingProducts(StripeProductType.ADD_ON)
      .subscribe({
        next: (products) => {
          atsAll = products.filter((product) => product.unit_amount > 0);
          catalogLoaded();
        },
        error: () => catalogLoaded(),
      });
  }

  /**
   * Splits catalogs into month/year cache entries.
   * @param {StripeProduct[]} baseAll Base prices
   * @param {StripeProduct[]} agentsAll Agents prices
   * @param {StripeProduct[]} atsAll ATS prices
   * @returns {void}
   */
  private fillCatalogCache(
    baseAll: StripeProduct[],
    agentsAll: StripeProduct[],
    atsAll: StripeProduct[],
  ): void {
    for (const interval of [
      StripeBillingInterval.MONTH,
      StripeBillingInterval.YEAR,
    ]) {
      this.catalogCache.set(interval, {
        base: this.sortBaseProducts(
          baseAll.filter(
            (product) =>
              !product.recurring?.interval ||
              product.recurring.interval === interval,
          ),
        ),
        agents: agentsAll.filter(
          (product) => product.recurring?.interval === interval,
        ),
        ats: atsAll.filter(
          (product) => product.recurring?.interval === interval,
        ),
      });
    }
  }

  /**
   * Applies one cached catalog slice to the selectors.
   * @param {{ base: StripeProduct[]; agents: StripeProduct[]; ats: StripeProduct[] }} slice Catalog slice
   * @returns {void}
   */
  private applyCatalogSlice(slice: {
    base: StripeProduct[];
    agents: StripeProduct[];
    ats: StripeProduct[];
  }): void {
    this.baseProducts.set(slice.base);
    this.agentsProducts.set(slice.agents);
    this.atsProducts.set(slice.ats);
  }

  /**
   * Rematches selection after a month/year switch.
   * @param {object} previous Selection before the tab switch
   * @returns {void}
   */
  private restoreSelectionAfterIntervalSwitch(previous: {
    previousBase: StripeProduct | null;
    previousAgents: StripeProduct | null;
    previousAts: StripeProduct | null;
    previousAgentsOption: string | null;
    previousCount: number;
    previousIncludeAgents: boolean;
    previousIncludeAts: boolean;
  }): void {
    this.includeAgents.set(previous.previousIncludeAgents);
    this.includeAts.set(previous.previousIncludeAts);

    if (previous.previousBase?.id === COMBINED_FREE_PRICE_ID) {
      this.selectedBasePriceId.set(COMBINED_FREE_PRICE_ID);
    } else if (previous.previousBase) {
      const level = +(previous.previousBase.metadata?.sub_level ?? 0);
      const matched =
        this.baseProducts().find(
          (product) => +(product.metadata?.sub_level ?? 0) === level,
        ) ?? null;
      this.selectedBasePriceId.set(
        matched?.id ?? this.defaultBasePriceId(this.baseProducts()),
      );
    } else {
      this.selectedBasePriceId.set(this.defaultBasePriceId(this.baseProducts()));
    }

    if (previous.previousAgentsOption === this.CUSTOM_AGENTS_OPTION) {
      this.selectedAgentsOption.set(this.CUSTOM_AGENTS_OPTION);
      this.customAgentCount.set(Math.max(1, previous.previousCount));
    } else if (previous.previousAgents) {
      const seats = this.maxAgentsOf(previous.previousAgents);
      const matched =
        this.agentsProducts().find(
          (product) => this.maxAgentsOf(product) === seats,
        ) ?? null;
      this.selectedAgentsOption.set(
        matched?.id ??
          (this.singleAgentProduct()
            ? this.CUSTOM_AGENTS_OPTION
            : (this.agentsProducts()[0]?.id ?? null)),
      );
      this.customAgentCount.set(
        this.selectedAgentsOption() === this.CUSTOM_AGENTS_OPTION
          ? Math.max(1, previous.previousCount)
          : 1,
      );
    } else {
      this.selectedAgentsOption.set(
        this.singleAgentProduct()
          ? this.CUSTOM_AGENTS_OPTION
          : (this.agentsProducts()[0]?.id ?? null),
      );
      this.customAgentCount.set(1);
    }

    if (previous.previousAts) {
      const name = (previous.previousAts.product?.name ?? '').toLowerCase();
      const matched =
        this.atsProducts().find(
          (product) =>
            (product.product?.name ?? '').toLowerCase() === name,
        ) ?? this.atsProducts()[0];
      this.selectedAtsPriceId.set(matched?.id ?? null);
    } else {
      this.selectedAtsPriceId.set(this.atsProducts()[0]?.id ?? null);
    }
  }

  /**
   * Sorts paid Base plans by subscription level and price.
   * @param {StripeProduct[]} products Base catalog products
   * @returns {StripeProduct[]}
   */
  private sortBaseProducts(products: StripeProduct[]): StripeProduct[] {
    const paidProducts = products
      .filter((product) => product.unit_amount > 0)
      .sort((first, second) => {
        const levelDifference =
          +(first.metadata?.sub_level ?? 0) -
          +(second.metadata?.sub_level ?? 0);
        return levelDifference || first.unit_amount - second.unit_amount;
      });
    return [...paidProducts, COMBINED_FREE_BASE_PRODUCT];
  }

  /**
   * Finds a product by price id.
   * @param {StripeProduct[]} products Catalog products
   * @param {string | null} priceId Selected price id
   * @returns {StripeProduct | null}
   */
  private findProduct(
    products: StripeProduct[],
    priceId: string | null,
  ): StripeProduct | null {
    return products.find((product) => product.id === priceId) ?? null;
  }

  /**
   * Current package interval from the live summary.
   * @returns {StripeBillingInterval | null}
   */
  private currentPackageInterval(): StripeBillingInterval | null {
    const summary = this.subscription()?.subscriptionSummary;
    const interval =
      summary?.baseSubscription?.interval ??
      summary?.agentsSubscription?.interval ??
      summary?.atsSubscription?.interval;
    if (interval === StripeBillingInterval.YEAR) {
      return StripeBillingInterval.YEAR;
    }
    if (interval === StripeBillingInterval.MONTH) {
      return StripeBillingInterval.MONTH;
    }
    return null;
  }

  /**
   * Whether the selection differs from the live package.
   * @returns {boolean}
   */
  private hasSelectionChanges(): boolean {
    const summary = this.subscription()?.subscriptionSummary;
    if (!summary) {
      return true;
    }
    const currentInterval = this.currentPackageInterval();
    if (currentInterval && currentInterval !== this.billingInterval()) {
      return true;
    }
    if (this.selectedBasePriceId() !== this.currentBasePriceId()) {
      return true;
    }
    if (this.includeAgents() !== this.currentlyHasAgents()) {
      return true;
    }
    if (this.includeAgents()) {
      if (this.selectedAgentsOption() !== this.currentAgentsOption()) {
        return true;
      }
      if (
        this.selectedAgentsOption() === this.CUSTOM_AGENTS_OPTION &&
        this.customAgentCount() !== this.currentAgentSeats()
      ) {
        return true;
      }
    }
    if (this.includeAts() !== this.currentlyHasAts()) {
      return true;
    }
    if (
      this.includeAts() &&
      this.selectedAtsPriceId() !== this.currentAtsPriceId()
    ) {
      return true;
    }
    return false;
  }

  /**
   * Whether any chosen change must wait until period end.
   * Unchanged items and first paid-base adds are not later changes.
   * @returns {boolean}
   */
  private selectionHasLaterChange(): boolean {
    if (!this.hasPaidPackage()) {
      return false;
    }
    const currentInterval = this.currentPackageInterval();
    if (
      currentInterval === StripeBillingInterval.YEAR &&
      this.billingInterval() === StripeBillingInterval.MONTH
    ) {
      return true;
    }
    if (this.currentlyHasAgents() && !this.includeAgents()) {
      return true;
    }
    if (this.currentlyHasAts() && !this.includeAts()) {
      return true;
    }
    if (
      this.currentlyHasPaidBase() &&
      this.isFreeBase(this.selectedBaseProduct())
    ) {
      return true;
    }
    const baseProduct = this.selectedBaseProduct();
    if (
      this.currentlyHasPaidBase() &&
      baseProduct &&
      currentInterval &&
      this.selectedBasePriceId() !== this.currentBasePriceId()
    ) {
      const currentLevel = this.currentBaseLevel();
      const nextLevel = +(baseProduct.metadata?.sub_level ?? 0);
      const currentAmount =
        this.subscription()?.subscriptionSummary?.baseSubscription?.cost ?? 0;
      if (
        !isImmediatePlanChange(
          currentInterval,
          currentLevel,
          this.billingInterval(),
          nextLevel,
          currentAmount,
          baseProduct.unit_amount,
        )
      ) {
        return true;
      }
    }
    const agentsProduct = this.selectedAgentsProduct();
    if (
      this.includeAgents() &&
      this.currentlyHasAgents() &&
      agentsProduct &&
      this.agentsSelectionChanged()
    ) {
      const quantity =
        this.selectedAgentsOption() === this.CUSTOM_AGENTS_OPTION
          ? this.customAgentCount()
          : 1;
      const nextSeats =
        this.maxAgentsOf(agentsProduct) === 1
          ? quantity
          : this.maxAgentsOf(agentsProduct);
      const currentAmount =
        this.subscription()?.subscriptionSummary?.agentsSubscription?.cost ?? 0;
      if (
        currentInterval &&
        !isImmediatePlanChange(
          currentInterval,
          this.currentAgentSeats(),
          this.billingInterval(),
          nextSeats,
          currentAmount,
          agentsProduct.unit_amount * quantity,
        )
      ) {
        return true;
      }
    }
    return false;
  }

  /**
   * Whether the Agents selector differs from the live Agents item.
   * @returns {boolean}
   */
  private agentsSelectionChanged(): boolean {
    if (this.selectedAgentsOption() !== this.currentAgentsOption()) {
      return true;
    }
    return (
      this.selectedAgentsOption() === this.CUSTOM_AGENTS_OPTION &&
      this.customAgentCount() !== this.currentAgentSeats()
    );
  }

  /**
   * Prefills selectors from the live subscription.
   * Growth is only preselected when nothing is booked yet
   * (no paid base, no Agents, no ATS add-on).
   * @returns {void}
   */
  private prefillFromSummary(): void {
    const baseProducts = this.baseProducts();
    this.selectedBasePriceId.set(this.defaultBasePriceId(baseProducts));

    const agentsProducts = this.agentsProducts();
    const customFallback = agentsProducts.some(
      (product) => this.maxAgentsOf(product) === 1,
    )
      ? this.CUSTOM_AGENTS_OPTION
      : (agentsProducts[0]?.id ?? null);
    if (this.currentlyHasAgents()) {
      this.includeAgents.set(true);
      const option = this.currentAgentsOption() ?? customFallback;
      this.selectedAgentsOption.set(option);
      this.customAgentCount.set(
        option === this.CUSTOM_AGENTS_OPTION
          ? Math.max(1, this.currentAgentSeats())
          : 1,
      );
    } else {
      this.includeAgents.set(false);
      this.selectedAgentsOption.set(customFallback);
      this.customAgentCount.set(1);
    }

    const atsProducts = this.atsProducts();
    if (this.currentlyHasAts()) {
      this.includeAts.set(true);
      this.selectedAtsPriceId.set(
        this.currentAtsPriceId() ?? atsProducts[0]?.id ?? null,
      );
    } else {
      this.includeAts.set(false);
      this.selectedAtsPriceId.set(atsProducts[0]?.id ?? null);
    }
  }

  /**
   * Matches the live base plan in the loaded catalog.
   * @returns {string | null}
   */
  private currentBasePriceId(): string | null {
    const summary = this.subscription()?.subscriptionSummary;
    if (!summary?.baseSubscription?.name || summary.isFree) {
      return COMBINED_FREE_PRICE_ID;
    }
    return (
      summary.baseSubscription.priceId ??
      this.matchProductId(
        this.baseProducts(),
        summary.baseSubscription.name,
        +(summary.baseSubscription.maxJobs ?? 0),
        'max_jobs',
      )
    );
  }

  /**
   * Matches the live ATS price in the loaded catalog.
   * @returns {string | null}
   */
  private currentAtsPriceId(): string | null {
    const summary = this.subscription()?.subscriptionSummary;
    const ats = summary?.atsSubscription;
    if (!ats?.name) {
      return null;
    }
    return (
      ats.priceId ??
      this.matchProductId(this.atsProducts(), ats.name, 0, null)
    );
  }

  /**
   * Live Agents selector value (catalog price or custom).
   * @returns {string | null}
   */
  private currentAgentsOption(): string | null {
    const agents =
      this.subscription()?.subscriptionSummary?.agentsSubscription;
    if (!agents?.name) {
      return null;
    }
    const priceId = agents.priceId ?? null;
    if (priceId) {
      if (this.singleAgentProduct()?.id === priceId) {
        return this.CUSTOM_AGENTS_OPTION;
      }
      if (this.agentsProducts().some((product) => product.id === priceId)) {
        return priceId;
      }
    }
    const seats = +(agents.maxAgents ?? 0);
    if (seats > 1) {
      const matched = this.agentsProducts().find(
        (product) => this.maxAgentsOf(product) === seats,
      );
      if (matched) {
        return matched.id;
      }
    }
    if (this.singleAgentProduct()) {
      return this.CUSTOM_AGENTS_OPTION;
    }
    return null;
  }

  /**
   * Live agent seat count.
   * @returns {number}
   */
  private currentAgentSeats(): number {
    const agents =
      this.subscription()?.subscriptionSummary?.agentsSubscription;
    if (!agents) {
      return 0;
    }
    if (agents.priceId && this.singleAgentProduct()?.id === agents.priceId) {
      return Math.max(1, agents.quantity ?? 1);
    }
    if (this.currentAgentsOption() === this.CUSTOM_AGENTS_OPTION) {
      return Math.max(1, agents.quantity ?? agents.maxAgents ?? 1);
    }
    return +(agents.maxAgents ?? 0);
  }

  /**
   * Live base sub_level, falling back to catalog match.
   * @returns {number}
   */
  private currentBaseLevel(): number {
    const product = this.findProduct(
      this.baseProducts(),
      this.currentBasePriceId(),
    );
    return +(product?.metadata?.sub_level ?? 0);
  }

  /**
   * Whether Agents is currently billed.
   * @returns {boolean}
   */
  private currentlyHasAgents(): boolean {
    return !!this.subscription()?.subscriptionSummary?.agentsSubscription?.name;
  }

  /**
   * Whether ATS is currently billed.
   * @returns {boolean}
   */
  private currentlyHasAts(): boolean {
    const ats = this.subscription()?.subscriptionSummary?.atsSubscription;
    return !!ats?.name && (ats.cost ?? 0) > 0;
  }

  /**
   * Default Base price: current paid plan, otherwise Growth only when
   * no Base, Agents, or ATS add-on is booked yet.
   * @param {StripeProduct[]} baseProducts Base catalog products
   * @returns {string}
   */
  private defaultBasePriceId(baseProducts: StripeProduct[]): string {
    if (this.currentlyHasPaidBase()) {
      return this.currentBasePriceId() ?? COMBINED_FREE_PRICE_ID;
    }
    if (this.currentlyHasAgents() || this.currentlyHasAts()) {
      return COMBINED_FREE_PRICE_ID;
    }
    return (
      this.findGrowthProduct(baseProducts)?.id ??
      baseProducts.find((product) => !this.isFreeBase(product))?.id ??
      COMBINED_FREE_PRICE_ID
    );
  }

  /**
   * Finds the recommended Growth catalog price.
   * @param {StripeProduct[]} products Base catalog products
   * @returns {StripeProduct | undefined}
   */
  private findGrowthProduct(
    products: StripeProduct[],
  ): StripeProduct | undefined {
    return products.find((product) => {
      if (this.isFreeBase(product)) {
        return false;
      }
      const normalizedName = (product.product?.name ?? '').toLowerCase();
      if (normalizedName.includes('growth')) {
        return true;
      }
      return +(product.metadata?.sub_level ?? 0) === 2;
    });
  }

  /**
   * Whether the company currently pays for a base plan.
   * @returns {boolean}
   */
  private currentlyHasPaidBase(): boolean {
    const summary = this.subscription()?.subscriptionSummary;
    if (!summary || summary.isFree) {
      return false;
    }
    return (summary.baseSubscription?.cost ?? 0) > 0;
  }

  /**
   * Whether the selected base plan is Free (no Stripe BASIC item).
   * @param {StripeProduct | null} product Selected base catalog product
   * @returns {boolean}
   */
  private isFreeBase(product: StripeProduct | null): boolean {
    return (
      !!product &&
      (product.id === COMBINED_FREE_PRICE_ID || product.unit_amount === 0)
    );
  }

  /**
   * Finds a catalog price by product name, then by a metadata slot count.
   * @param {StripeProduct[]} products Catalog
   * @param {string} name Live plan name
   * @param {number} slotCount Live slot count
   * @param {string | null} metadataKey Slot metadata key
   * @returns {string | null}
   */
  private matchProductId(
    products: StripeProduct[],
    name: string,
    slotCount: number,
    metadataKey: string | null,
  ): string | null {
    const normalized = name.toLowerCase();
    const byName = products.find(
      (product) => (product.product?.name ?? '').toLowerCase() === normalized,
    );
    if (byName) {
      return byName.id;
    }
    if (metadataKey && slotCount > 0) {
      const bySlots = products.find(
        (product) => +(product.metadata?.[metadataKey] ?? 0) === slotCount,
      );
      if (bySlots) {
        return bySlots.id;
      }
    }
    return null;
  }

  /**
   * Resets modal state while keeping the preferred yearly interval.
   * @returns {void}
   */
  private reset(): void {
    this.paymentCheckout()?.destroy();
    this.allowUnchangedSelection.set(false);
    this.billingInterval.set(StripeBillingInterval.YEAR);
    this.includeAgents.set(false);
    this.includeAts.set(false);
    this.customAgentCount.set(1);
    this.selectedBasePriceId.set(null);
    this.selectedAgentsOption.set(null);
    this.selectedAtsPriceId.set(null);
    this.selectionLocked.set(false);
    this.catalogCache.clear();
    this.catalogRequestId++;
    this.isPaymentStep.set(false);
    this.isPaymentMethodStep.set(false);
    this.bookingInProgress.set(false);
    this.pendingCheckoutSessionId = null;
    this.pendingCheckoutPriceId = null;
    this.pendingCheckoutQuantity = 1;
    this.pendingPackageItems = [];
  }
}
